import { reactive, ref, watch } from 'vue';
import { Bus, useAside, useLoader, useModal, useNotif } from '@orion.ui/orion';
import BaseSetupService from '@/setup/BaseSetupService';
import SelfEntity from '@/entity/SelfEntity';
import useMembershipService from '@/services/MembershipService';
// import OnboardingTempWorkerSignedUpModal from '@/components/onboarding/OnboardingTempWorkerSignedUpModal.vue';
import useUserNewsService from '@/services/UserNewsService';
import useBusService from '@/services/BusService';
import ModalReleaseNote from '@/components/modal/ModalReleaseNote.vue';
import { SETTING_TYPE } from '@/typings/enums/SETTING_TYPE';
import Broadcast from '@/signalr/Broadcast.signalr';
import useOrganizationRoleService from '@/services/OrganizationRoleService';
import OrganizationRoleListItemEntity from '@/entity/OrganizationRoleListItemEntity';
import useTagUserService from '@/services/tag/TagUserService';

import OrganizationUserAsideDetails from '@/components/organization-user/OrganizationUserAsideDetails.vue';
import { SUITE_URL } from '@/utils/CONST';

type Props = SetupProps<typeof SharedLayoutSetupService.props>
export default class SharedLayoutSetupService extends BaseSetupService<Props> {
	static readonly props = {};

	private readonly state = reactive({
		scrollDirection: 'down' as 'down' | 'up',
		unreadNewsCount: 0,
		roles: [] as OrganizationRoleListItemEntity[],
		tags: [] as ITagUserSelfModel[],
		loading: false,
		showPermissionTooltip: false,
	});

	readonly _navigationLoader = ref<OrionLoader>();
	readonly _armNav = ref<HTMLElement>();
	readonly _asideMobileNav = ref<OrionAside>();

	private notif: { [id:number]: OrionNotif } = {};

	get scrollDirection () { return this.state.scrollDirection; }
	get unreadNewsCount () { return this.state.unreadNewsCount < 0 ? 0 : this.state.unreadNewsCount; }
	get roles () { return this.state.roles; }
	get tags () { return this.state.tags; }
	get loading () { return this.state.loading; }
	get showPermissionTooltip () { return this.state.showPermissionTooltip; }
	set showPermissionTooltip (val) { this.state.showPermissionTooltip = val; }

	constructor (props: Props) {
		super(props);
		this.initAppAsync();
		Bus.on('navigation-loader:show', message => this._navigationLoader.value?.show(message as string));
		Bus.on('navigation-loader:hide', () => this._navigationLoader.value?.hide());

		this.registerBusEvent('toggle-left-aside', (val?: boolean) => this.toggleLeftAside(val));
		this.registerBusEvent('toggle-right-aside', (val?: boolean) => this.toggleRightAside(val));
		this.registerBusEvent('open-aside-mobile-nav', (val: boolean) => {
			val ? this._asideMobileNav.value?.open() : this._asideMobileNav.value?.close();
		});

		useBusService().on('user-news:read', (ids: number[]) => this.state.unreadNewsCount -= ids.length);

		watch([
			() => this.route.name,
			() => this.route.params,
		],
		() => {
			this._asideMobileNav.value?.close();

			if (this.ui.onLayoutNoAside || this.ui.onLayoutOneAside) {
				if (this.route.name !== 'TimeTicketWeek') {
					this.ui.asideLeftSlideOut = false;
					this.ui.asideRightSlideOut = false;
				}
			}
		});
	}


	async initAppAsync () {
		useLoader().show(`Chargement de votre compte...`);

		Broadcast.onAddOrUpdate(this.showAdminNotification.bind(this));
		Broadcast.onDelete(this.deleteAdminNotification.bind(this));

		await SelfEntity.getUserInfosAsync();
		await this.checkReleaseNoteAsync();
		await this.getUnreadNewsCountAsync();
		await useMembershipService().getRelatedAgenciesAsync();

		this.self.isFullyLoaded();

		// #region OnBoarding
		if (this.self.isTempWorker() && this.self.isConnected) {
			const agencyCompanyCode = JSON.parse(localStorage.getItem('agency-company-code') ?? '{}');
			// const hasAgencies = !!useMembershipService().relatedAgencies.length;

			if (this.route.query.agencyCompanyCode || (agencyCompanyCode.code && new Date() < new Date(agencyCompanyCode.expirationDate))) {
				const code = this.route.query.agencyCompanyCode ?? agencyCompanyCode.code;
				useMembershipService().openTempWorkerAddMembershipModalAsync(code);
			} /* // disabled for now because it generates additional support requests
      else if (!hasAgencies) {
				if (localStorage.getItem('display-onboarding-temp-worker-signed-up-modal')) {
					useModal({ Nested: OnboardingTempWorkerSignedUpModal });
				} else {
					useMembershipService().openTempWorkerNoMembershipModalAsync();
				}
			} */

			if (agencyCompanyCode.code && new Date() > new Date(agencyCompanyCode.expirationDate)) {
				localStorage.removeItem('agency-company-code');
			}
		}
		// #endregion


		useLoader().hide();
	}

	toggleLeftAside (open?: boolean) {
		this.ui.setAsideLeftSlideOut(open ?? !this.ui.asideLeftSlideOut);
	}

	toggleRightAside (open?: boolean) {
		this.ui.setAsideRightSlideOut(open ?? !this.ui.asideRightSlideOut);
	}

	private async getUnreadNewsCountAsync () {
		if (this.self.isAgency()) return;
		this.state.unreadNewsCount = await useUserNewsService().countNotReadAsync();
	}

	async checkReleaseNoteAsync () {
		if (this.self.isTempWorker()) return;

		const noRelease = await this.self.getUserSettingAsync(SETTING_TYPE.ReleaseNote);

		if (noRelease === false) {
			useModal({
				Nested: ModalReleaseNote,
				hideClose: true,
				hideOnOverlayClick: false,
				hideOnEsc: false,
			});
		}
	}

	protected onUnmounted (): void {
		Broadcast.offAddOrUpdate(this.showAdminNotification.bind(this));
		Broadcast.offDelete(this.deleteAdminNotification.bind(this));
	}

	showAdminNotification (model: IBroadcastModel) {
		if (this.notif[model.id]) {
			this.notif[model.id].close();
		}

		const options = {
			duration: null,
			title: model.title,
			message: model.message,
			icon: model.icon ? model.icon as Orion.Icon : 'triangle_warning',
		};

		if (Object.keys(useNotif).includes(model.color as any)) {
			const color = model.color as keyof typeof useNotif;

			this.notif[model.id] = useNotif[color](options);
		} else {
			this.notif[model.id] = useNotif.warning(options);
		}
	}

	deleteAdminNotification (id : number) {
		if (this.notif[id]) {
			this.notif[id].close();
		}
	}

	async handlePopoverShowAsync () {
		this.state.loading = true;
		this.state.showPermissionTooltip = true;
		this.state.roles = OrganizationRoleListItemEntity.map(await useOrganizationRoleService().selfAsync());
		this.state.tags = await useTagUserService().selfAsync();
		this.state.loading = false;
	}

	handlePopoverHide () {
		this.state.showPermissionTooltip = false;
	}

	openUserRolesAside () {
		this.state.showPermissionTooltip = false;
		useAside({
			Nested: OrganizationUserAsideDetails,
			NestedProps: {
				id: this.self.organizationUserId,
				readonly: true,
				self: true,
			},
			size: 'lg',
		});
	}

	showUserProfile () {
		if (this.self.suiteUserId)
			window.location.href = SUITE_URL + `/profil/general?source=Armado&sourceUrl=${location.href}`;
		else {
			this.router.push({ name: 'UserProfileInformations' });
		}
	}
}
